import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, filter, finalize, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoadingService } from './shared/loading/loading.service';
import { BaseService } from './shared/services/base.service';
import { StorageUtils } from './shared/utils/storage-utils';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  exclude = ['N0001', 'N0002', 'N0003', 'N0004', 'N0005', 'B80', 'B45', 'B46', 'A0002'];

  redirect = ['B57', 'S0001'];

  userInfoConfirm = ['B39', 'B41', 'B40', 'B42'];

  errCodes :Array<string> = [];

  canModifyCode =
    [ 'B01', 'B02', 'B03', 'B04', 'B05', 'B06', 'B07', 'B22', 'B28', 'B29', 'B30', 'B31', 'B39', 'B40', 'B41', 'B42', 'B45', 'B46', 'B58', 'B60', 'B61', 'B64', 'B65', 'B66', 'B67', 'B72', 'B79', 'B80', 'B81',
      'C01', 'C02', 'C03', 'C04', 'C05', 'C06', 'C24', 'C25', 'C26', 'C27', 'C28', 'C29', 'C30', 'C31', 'C32', 'C33', 'C34', 'C35', 'C36', 'A0001', '0001', 'V0004', 'V0005' ]

  constructor(
    private httpClient: HttpClient,
    private loadingService: LoadingService,
    private router: Router,
    private baseService: BaseService,
  ) { }

  /**
   * GET 請求 (用於查詢操作)
   * @param path 路徑
   * @param options 選項
   */
  getRequest(path: string, options?: object) {
    const url = environment.API_SERVER_URL + path;
    return this.httpClient.get(url, options ? options : this.getDefaultHttpOptions())
      .pipe(
        catchError(err => {
          this.httpErrorHandler(err);
          throw err;
        })
      );
  }

  /**
   * POST 請求 (用於查詢操作)
   * @param path 路徑
   * @param body 內容參數
   * @param options 選項
   *
   */
  postRequest(path: string, body: any, option?: any) {
    const url = environment.API_SERVER_URL + path;
    this.loadingService.show();
    let headers = new HttpHeaders();
    const csrf = this.getCookie('XSRF-TOKEN');
    if(csrf){
      headers = headers.append('X-XSRF-TOKEN',csrf);
    }
    return this.httpClient.post<any>(url, body, {headers: headers})
      .pipe(
        tap(res => {
          //身份驗證直接導頁
          if(null != res.payload.body && res.payload.body.isCertification){
            window.location.href = environment.WEBINSURANCE_API_URL+'Certification';
          }

          if(res.code){
           this.errCodes =  res.code.replace(/[\[\]]/g, '').split(',').filter(v => v);
          }
          if (!res.succeeded && this.errCodes.filter(tempcode => this.exclude.concat(this.userInfoConfirm).indexOf(tempcode.trim())<0).length>0){
            console.log(res.code + ':' + res.message);
            alert(res.message);
            if ( 'P0002' === res.code ) {
              this.router.navigateByUrl(this.router.url.slice(0, -1).concat('1'));
            } else if ( this.checkRedirectIndex() ) {
              window.location.href = this.baseService.info.indexUrl;
              throw new Error('停止此作業階段');
            }
          }

        }),
        filter(res => res.succeeded || (!res.succeeded && this.errCodes.filter( tempcode=> this.exclude.concat(this.userInfoConfirm).indexOf(tempcode.trim()) > -1 ).length >0 )),
        catchError(err => {
          console.log(err);
          this.httpErrorHandler(err);
          throw err;
        }),
        finalize(() => {
          this.loadingService.hide();
        }),
      );
  }

  getFirstLifeRequest(path: string, options?: object) {
    const url = environment.FIRST_LIFE_INSURANCE_SERVER_URL + path;
    return this.httpClient.get(url, options ? options : this.getDefaultHttpOptions())
      .pipe(
        catchError(err => {
          this.httpErrorHandler(err);
          throw err;
        })
      );
  }

  /**
   * 取得預設的請求表頭
   */
  getDefaultHttpOptions() {
    const accessToken = this.getAccessToken();
    const headerObject = {
      Authorization: (() => {
        if (accessToken) {
          return 'Bearer ' + accessToken;
        } else {
          return '';
        }
      })(),
      'X-XSRF-TOKEN':this.getCookie('XSRF-TOKEN'),
    };
    const httpOptions = {
      headers: new HttpHeaders(headerObject)
    };
    return httpOptions;
  }

  /**
   * 取得預設的請求表頭
   */
  getDefaultHttpOptionsForToken(token: any) {
    const accessToken = token;
    const headerObject = {
      Authorization: (() => {
        if (accessToken) {
          return 'Bearer ' + accessToken;
        } else {
          return '';
        }
      })()
    };
    const httpOptions = {
      headers: new HttpHeaders(headerObject)
    };
    return httpOptions;
  }

  /**
   * 取得檔案傳輸預設的請求表頭
   */
  getDefaultFileOptions() {
    const httpOptions = this.getDefaultHttpOptions();
    const fileHttpOptions = Object.assign({}, httpOptions, {
      reportProgress: true,
      observe: 'events'
    });
    return fileHttpOptions;
  }

  /**
   * Http錯誤捕捉
   */
  httpErrorHandler(error: HttpErrorResponse) {
    switch (error.status) {
      case 401:
      case 403:
        let fundrichVal = window.location.href?.includes('/fundrich/') ? '&fundrich=true' : '';
        let url = window.location.href?.includes('/taa/') ? window.location.href.replace(/step\d+/,'step11') : window.location.href.replace(/(\/step)\d+$/,'$11');
        if (error.error.status === 'SESSION_EXPIRED') {
          // Timeout 強制 logout
          this.router.navigateByUrl('/login?redirect_uri=' + encodeURIComponent(url) + fundrichVal + '&timeout=true');
        } else if (error.error.status === 'NOT_LOGGED_IN') {
          // 首次未登入
          this.router.navigateByUrl('/login?redirect_uri=' + encodeURIComponent(url) + fundrichVal + '&timeout=false');
        }

        break;
      case 408:
        alert('您超過使用時間已被登出');
        window.location.href = this.baseService.info.indexUrl;
        break;
      case 409:
        alert('您的帳號已在其它地點登入');
        window.location.href = this.baseService.info.indexUrl;
        break;
    }
  }

  /** 取得使用者登入的Access Token */
  getAccessToken(): string {
    // const token = this.cookieService.get(AuthConstants.ACCESS_TOKEN);
    // super.info(AuthConstants.ACCESS_TOKEN, token);
    // return token;
    return 'token';
  }

  checkRedirectIndex(): boolean {
    const result = this.errCodes.filter(( e ) => this.canModifyCode.indexOf(e) > -1);
    return this.errCodes?.length > result.length;
  }

  getCookie(name: string) {
    let maxLength = Number.MAX_SAFE_INTEGER;
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    if(caLen > maxLength){caLen = maxLength}; // 弱掃:Unchecked Input For Loop Condition
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }
}
